/**
 * <cross-sell-recommendations>
 *
 * Used by the PDP "complementary products" block and the cart drawer cross-sell.
 *
 * Pinned products (a merchant-selected product_list) are rendered server-side via the
 * `product-cross-sell` snippet and appear FIRST inside the carousel. This element then
 * fetches Shopify's learned recommendations (intent="related") via the Section Rendering
 * API and APPENDS the server-rendered `.product-cross-sell` cards after the pinned ones,
 * so the design stays pixel-identical (no cards are rebuilt in JS).
 *
 * Attributes:
 *   product       - seed product id (PDP product, or first cart item)
 *   limit         - desired number of cards total in the carousel
 *   section-id    - section to render via /recommendations/products
 *   intent        - "related"
 *   carousel-id   - id of the inner <scroll-carousel>
 *   controls-id   - id of the prev/next nav wrapper (revealed when scrollable)
 *   exclude-ids   - comma-separated product ids to never append (pinned + cart + current)
 */
class CrossSellRecommendations extends HTMLElement {
  connectedCallback() {
    if (this._loaded) return;
    this._loaded = true;
    this.loadRecommendations();
  }

  get carousel() {
    return this.querySelector('scroll-carousel');
  }

  get controls() {
    return this.querySelector('[id$="-controls"]');
  }

  /** Reveal/remove the block and re-init the carousel + nav once cards are settled. */
  finalize() {
    const carousel = this.carousel;
    const total = carousel ? carousel.querySelectorAll('.product-cross-sell').length : 0;

    if (total === 0) {
      // No pinned products and no recommendations -> nothing to show.
      this.remove();
      return;
    }

    this.hidden = false;
    this.updateControlsVisibility();

    if (carousel && !this._resizeObserver) {
      // The cart drawer may still be closed (or mid-open animation) when the
      // recommendations land, in which case the carousel measures 0x0 and the
      // scrollable check is meaningless. Re-check whenever its box size changes.
      this._resizeObserver = new ResizeObserver(() => this.updateControlsVisibility());
      this._resizeObserver.observe(carousel);
    }
  }

  /** Show the prev/next controls only when the carousel actually overflows. */
  updateControlsVisibility() {
    const carousel = this.carousel;
    const controls = this.controls;
    if (!carousel || !controls) return;

    // Not laid out yet (drawer closed/animating): keep the current state and let
    // the ResizeObserver re-run this once the carousel has a real size.
    if (carousel.clientWidth === 0) return;

    controls.hidden = carousel.scrollWidth - carousel.clientWidth <= 1;
  }

  /**
   * ScrollCarousel wires its scroll/scrollend listeners in its constructor, and only
   * when it has more than one cell at that moment. A carousel that starts with 0 or 1
   * pinned cards therefore never emits the scroll:edge-* events that enable/disable
   * the prev/next buttons, leaving the prev button permanently disabled. Re-creating
   * the element after all cards are appended runs the constructor against the final
   * cell list, and re-inserting the nav buttons re-binds them to the new instance.
   */
  rebuildCarousel() {
    const carousel = this.carousel;
    if (!carousel) return;

    carousel.replaceWith(carousel.cloneNode(true));

    const controls = this.controls;
    if (controls) {
      const anchor = document.createComment('');
      controls.replaceWith(anchor);
      anchor.replaceWith(controls);
    }
  }

  async loadRecommendations() {
    const carousel = this.carousel;
    const productId = this.getAttribute('product');

    const excludeIds = new Set(
      (this.getAttribute('exclude-ids') || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    );

    // Also exclude anything already rendered server-side (pinned cards).
    if (carousel) {
      carousel.querySelectorAll('[data-product-id]').forEach((card) => {
        const id = card.getAttribute('data-product-id');
        if (id) excludeIds.add(id);
      });
    }

    const limit = parseInt(this.getAttribute('limit'), 10) || 4;
    const pinnedCount = carousel ? carousel.querySelectorAll('.product-cross-sell').length : 0;
    const maxToAppend = Math.max(limit - pinnedCount, 0);

    // Nothing to fetch: empty seed (e.g. empty cart) or the carousel is already full of pinned.
    if (!productId || maxToAppend === 0 || !window.Shopify || !Shopify.routes) {
      this.finalize();
      return;
    }

    const sectionId = this.getAttribute('section-id') || '';
    const intent = this.getAttribute('intent') || 'related';
    // Request headroom so de-duplication doesn't starve the carousel (API caps at 10).
    const requestLimit = Math.min(limit + excludeIds.size, 10);
    const url =
      `${Shopify.routes.root}recommendations/products` +
      `?product_id=${encodeURIComponent(productId)}` +
      `&limit=${requestLimit}` +
      `&section_id=${encodeURIComponent(sectionId)}` +
      `&intent=${encodeURIComponent(intent)}`;

    try {
      const response = await fetch(url, { priority: 'low' });
      if (response.ok) {
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sourceCarousel = doc.getElementById(this.getAttribute('carousel-id'));
        const cards = (sourceCarousel || doc).querySelectorAll('.product-cross-sell');

        let appended = 0;
        cards.forEach((card) => {
          if (appended >= maxToAppend || !carousel) return;
          const id = card.getAttribute('data-product-id');
          if (id && excludeIds.has(id)) return;
          if (id) excludeIds.add(id);
          carousel.appendChild(document.importNode(card, true));
          appended += 1;
        });

        if (appended > 0) this.rebuildCarousel();
      }
    } catch (e) {
      /* best effort: leave pinned products in place */
    }

    this.finalize();
  }
}

if (!window.customElements.get('cross-sell-recommendations')) {
  window.customElements.define('cross-sell-recommendations', CrossSellRecommendations);
}
